"use client"

import { ProductCard } from "@/components/product-card"
import { useEffect, useState } from "react"
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel"
import { Skeleton } from "@/components/ui/skeleton"

export function FeaturedProducts({ hideHeading = false, hideViewAll = false }: { hideHeading?: boolean, hideViewAll?: boolean }) {
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchProducts() {
      setLoading(true)
      setError(null)
      try {
        // Fetch recently added products (no 'featured=true' filter)
        const res = await fetch("/api/products")
        if (!res.ok) throw new Error("Failed to fetch products")
        const data = await res.json()
        setProducts(data)
      } catch (err: any) {
        setError(err.message || "Unknown error")
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [])

  if (loading) {
    return (
      <section className="py-16 lg:py-20 xl:py-24">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="text-center mb-12 lg:mb-16 xl:mb-20">
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="space-y-3 p-4 border rounded-lg">
                <Skeleton className="h-48 w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-8 w-20 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-16 lg:py-20 xl:py-24">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Recently Added Products</h2>
          <p className="text-lg lg:text-xl xl:text-2xl text-red-500 mb-8 lg:mb-12">{error}</p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-4 md:py-6 bg-muted">
      <div className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        {!hideHeading && (
          <div className="text-center mb-12 lg:mb-16 xl:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Recently Added Products</h2>
            <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
              Check out the latest additions to our catalog
            </p>
          </div>
        )}
        <div className="relative">
          <Carousel className="w-full">
            <CarouselContent>
              {products.map((product) => {
                let parsedImages = []
                if (product.images) {
                  try {
                    parsedImages = typeof product.images === 'string' ? JSON.parse(product.images) : product.images
                  } catch (error) {
                    console.error('Error parsing product images:', error)
                    parsedImages = []
                  }
                }
                return (
                  <CarouselItem key={product.id} className="basis-72 max-w-xs">
                    <ProductCard product={{ ...product, images: parsedImages }} />
                  </CarouselItem>
                )
              })}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
        {/* No View All for products, so nothing to hide here, but prop for consistency */}
      </div>
    </section>
  )
}
